import React, { useState, useEffect, useRef } from 'react';
import { XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LineChart, Line } from 'recharts';
import { Settings, LogOut, ChartNoAxesCombined, X, Cloud, CloudRain, CloudLightning, Sun } from 'lucide-react';
import './Dashboardv3Page.css';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import logo from '../../logo.svg';
import { dashboardService } from '../../services/dashboardService';
import { useAuth } from '../../context/AuthContext';
import TideChart from '../../components/TideChart';

const LOCATIONS = [
    { id: 'PH1', name: 'PULAU MELAKA', lat: 2.1824, lon: 102.2530 },
    { id: 'PH2', name: 'PULAU KELAPA', lat: 2.1810, lon: 102.2610 },
    { id: 'PH3', name: 'BUNGA RAYA', lat: 2.201888, lon: 102.251525 },
];

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const LeafletMap = ({ markers = [] }) => {
    const mapContainerRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const tileLayerRef = useRef(null);
    const markersLayerRef = useRef(null);
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
        let isMounted = true;

        const loadLeaflet = () => {
            // Check for JS
            if (!window.L) {
                const script = document.createElement('script');
                script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
                script.async = true;
                script.onload = () => {
                    if (isMounted) initMap();
                };
                document.head.appendChild(script);
            } else {
                initMap();
            }
        };

        const initMap = async () => {
            if (!isMounted) return;
            if (mapInstanceRef.current) return;
            if (!mapContainerRef.current) return;
            if (!window.L) return;

            try {
                const map = window.L.map(mapContainerRef.current, {
                    maxZoom: 19,
                }).setView([2.19, 102.25], 12);
                mapInstanceRef.current = map;

                tileLayerRef.current = window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
                    maxZoom: 19
                }).addTo(map);

                // Fetch MBMB boundary from state.json
                try {
                    const response = await fetch('/state.json');
                    const geojsonData = await response.json();

                    // Filter for Majlis Bandaraya Melaka Bersejarah
                    const mbmbFeature = geojsonData.features.find(
                        f => f.properties.name === 'Majlis Bandaraya Melaka Bersejarah'
                    );

                    if (mbmbFeature) {
                        const boundaryLayer = window.L.geoJSON(mbmbFeature, {
                            style: {
                                color: '#9f1239',       // rose-800 border
                                weight: 3,
                                opacity: 0.8,
                                fillColor: '#9f1239',
                                fillOpacity: 0.06,
                                dashArray: '8, 5',
                            }
                        }).addTo(map);

                        // Fit map to the MBMB boundary and restrict panning
                        const bounds = boundaryLayer.getBounds();
                        map.fitBounds(bounds);
                        map.setMaxBounds(bounds.pad(0.1));
                        map.options.minZoom = map.getBoundsZoom(bounds.pad(0.1));
                    }
                } catch (err) {
                    console.error('Failed to load MBMB boundary:', err);
                }

                markersLayerRef.current = window.L.layerGroup().addTo(map);

                setIsLoaded(true);

                setTimeout(() => {
                    if (isMounted && map) map.invalidateSize();
                }, 200);

            } catch (error) {
                console.error("Map initialization failed:", error);
            }
        };

        loadLeaflet();

        return () => {
            isMounted = false;
            if (mapInstanceRef.current) {
                mapInstanceRef.current.remove();
                mapInstanceRef.current = null;
            }
        };
    }, []);


    useEffect(() => {

        if (!isLoaded || !markersLayerRef.current || !window.L) return;


        markersLayerRef.current.clearLayers();


        const createCustomIcon = (color, pulse = false) => {
            return window.L.divIcon({
                className: 'custom-div-icon',
                html: `<div style="background-color: ${color}; width: 12px; height: 12px; border-radius: 50%; border: 2px solid white; box-shadow: 0 0 10px ${color};" class="${pulse ? 'marker-pulse' : ''}"></div>`,
                iconSize: [12, 12],
                iconAnchor: [6, 6]
            });
        };

        // C. Loop through data and add new markers
        markers.forEach(item => {
            let position = null;
            if (item.position && item.position[0] && item.position[1]) {
                position = item.position;
            } else if (item.lat !== undefined && item.lon !== undefined) {
                position = [item.lat, item.lon];
            } else if (item.lat !== undefined && item.lng !== undefined) {
                position = [item.lat, item.lng];
            }

            if (!position || !position[0] || !position[1]) return;

            const color = item.color || '#3b82f6';
            const pulse = item.status === 'active' || item.status === 'warning';

            const marker = window.L.marker(position, {
                icon: createCustomIcon(color, pulse)
            })
                .addTo(markersLayerRef.current)
                .bindPopup(`<b>${item.name}</b><br>Status: ${item.status || 'Normal'}`);

            if (item.active) {
                marker.openPopup();
                mapInstanceRef.current.panTo(position);
            }
        });

    }, [markers, isLoaded]);

    return (
        <div className="relative w-full h-full">
            {!isLoaded && <div className="absolute inset-0 flex items-center justify-center bg-gray-100 text-gray-400 text-xs">Loading Map Interface...</div>}
            <div ref={mapContainerRef} className="w-full h-full min-h-[300px] z-0" />
        </div>
    );
};

const WaterWave = () => (
    <>
        <style>
            {`
                @keyframes wave {
                    0% { margin-left: 0; }
                    100% { margin-left: -100%; }
                }
            `}
        </style>
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
            {/* Main water body */}
            <div className="absolute bottom-0 w-full h-full bg-cyan-400/70"></div>
            {/* Two overlapping animated waves for the surface effect */}
            <div
                className="absolute -bottom-1 w-[200%] h-4 bg-cyan-300/50 rounded-[45%]"
                style={{ animationName: 'wave', animationDuration: '4s', animationTimingFunction: 'linear', animationIterationCount: 'infinite' }}
            />
            <div
                className="absolute -bottom-1 w-[200%] h-4 bg-cyan-400/60 rounded-[40%]"
                style={{ animationName: 'wave', animationDuration: '6s', animationTimingFunction: 'linear', animationIterationCount: 'infinite', animationDirection: 'reverse' }}
            />
        </div>
    </>
);

const WaterLevelGraphic = ({ levels, locationId }) => {
    const MAX_LEVEL = 2.0; // The max height in meters for calculating percentages

    // Dynamic labels based on location
    const getLabels = () => {
        switch (locationId) {
            case 'PH2':
                return { sea: 'MONSUN DRAIN', sump: 'DRAIN', drain: 'SUMP', tGate: ' ' };
            case 'PH3':
                return { sea: 'MONSUN DRAIN', sump: 'SUMP', drain: 'DRAIN', tGate: ' ' };
            default: // PH1
                return { sea: 'SEA', sump: 'SUMP', drain: 'DRAIN', tGate: 'T/Gate' };
        }
    };
    const labels = getLabels();

    const WaterColumn = ({ value, label }) => {
        const heightPercent = Math.min((value / MAX_LEVEL) * 100, 100);
        return (
            <div className="relative w-full h-full flex flex-col justify-end">
                {/* Water Fill */}
                <div
                    className="relative w-full transition-all duration-500"
                    style={{ height: `${heightPercent}%` }}
                >
                    {/* Water Value Text */}
                    <div className="absolute -top-5 w-full text-center text-xs font-bold text-black whitespace-nowrap">
                        {value.toFixed(2)}m
                    </div>
                    <WaterWave />
                </div>
                {/* Bottom Label */}
                <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 w-[90%] text-center">
                    <span className="text-[10px] font-semibold text-black bg-white/70 px-1.5 py-0.5 border border-gray-500 rounded leading-tight inline-block">
                        {label}
                    </span>
                </div>
            </div>
        );
    };

    return (
        <div className="relative w-full h-full">
            {/* Water Columns */}
            <div className="absolute top-[12.5%] left-0 w-full h-[75%] flex items-end z-10">
                <div className="w-1/3 h-full"><WaterColumn value={levels?.sea || 0} label={labels.sea} /></div>
                <div className="w-1/3 h-full mr-1"><WaterColumn value={levels?.sump || 0} label={labels.sump} /></div>
                <div className="w-1/3 h-full"><WaterColumn value={levels?.drain || 0} label={labels.drain} /></div>
            </div>

            {/* Dividers */}
            <div className="absolute top-0 left-[33.33%] h-[88%] w-2 bg-slate-600 rounded-t-sm z-20">
                <div className="absolute -top-5 text-xs text-black font-semibold whitespace-nowrap">{labels.tGate}</div>
            </div>
            <div className="absolute top-0 left-[66.66%] h-[88%] w-2 bg-slate-600 rounded-t-sm z-20">
                <div className="absolute -top-5 text-xs text-black font-semibold whitespace-nowrap">T/Screen</div>
            </div>

            {/* Threshold Lines */}
            {/* Position calculated as: top_offset + (1 - (level / MAX_LEVEL)) * water_area_height */}
            {/* DANGER (2.0m): 12.5% + (1 - 2.0/2.5) * 75% = 27.5% */}
            <div className="absolute top-[10.5%] w-full border-t-2 border-red-500 z-0"></div>
            {/* WARNING (1.5m): 12.5% + (1 - 1.5/2.0) * 75% = 42.5% */}
            <div className="absolute top-[29.25%] w-full border-t-2 border-orange-400 z-0"></div>
            {/* NORMAL (0.5m): 12.5% + (1 - 0.5/2.0) * 75% = 72.5% */}
            <div className="absolute top-[66.75%] w-full border-t-2 border-green-500 z-0"></div>
        </div>
    );
};

const ThresholdLegend = () => (
    <div className="space-y-1 mt-4">
        <div className="flex items-center justify-end"><span className="text-xs font-bold text-black pr-2">DANGER - 2.0m</span><div className="w-4 h-1 bg-red-500"></div></div>
        <div className="flex items-center justify-end"><span className="text-xs font-bold text-black pr-2">WARNING - 1.5m</span><div className="w-4 h-1 bg-orange-400"></div></div>
        <div className="flex items-center justify-end"><span className="text-xs font-bold text-black pr-2">NORMAL - 0.5m</span><div className="w-4 h-1 bg-green-500"></div></div>
    </div>
);


const PumpGauge = ({ name, value, status, onClick }) => {
    const getRotationForStatus = (status) => {
        switch (status?.toUpperCase()) {
            case 'RUN':
            case 'ON':
                return 60; // Point to green
            case 'STANDBY':
            case 'OFF':
                return 0; // Point to yellow
            case 'TRIP':
                return -60; // Point to red
            default:
                return 0;
        }
    };

    const rotation = getRotationForStatus(status);

    return (
        <div className="flex flex-col items-center">
            <div className="relative w-56 h-28 mb-2">
                {/* SVG for the gauge background */}
                <svg viewBox="0 0 100 50" className="w-full h-full">
                    {/* Red Section (TRIP) - Wider */}
                    <path d="M 10 50 A 40 40 0 0 1 30 15.36 L 45 36.34 A 10 10 0 0 0 40 50 Z" fill="#EF4444" />
                    {/* Yellow Section (STANDBY) - Wider */}
                    <path d="M 30 15.36 A 40 40 0 0 1 70 15.36 L 55 36.34 A 10 10 0 0 0 45 36.34 Z" fill="#FBBF24" />
                    {/* Green Section (RUN) - Wider */}
                    <path d="M 70 15.36 A 40 40 0 0 1 90 50 L 60 50 A 10 10 0 0 0 55 36.34 Z" fill="#10B981" />
                </svg>

                {/* Needle */}
                <div
                    className="absolute bottom-0 left-1/2 h-12 w-2 bg-gray-800 origin-bottom transition-transform duration-500"
                    style={{ transform: `translateX(-50%) rotate(${rotation}deg)`, clipPath: 'polygon(25% 0, 75% 0, 100% 100%, 0% 100%)' }}
                ></div>
                <div className="absolute bottom-[-8px] left-1/2 w-4 h-4 bg-gray-800 rounded-full transform -translate-x-1/2"></div>

                {/* Labels */}
                <div className="absolute top-[60%] left-[18%] text-xs font-bold bg-white border border-black px-0.5">TRIP</div>
                <div className="absolute top-[30%] left-1/2 -translate-x-1/2 text-xs font-bold bg-white border border-black px-0.5">STANDBY</div>
                <div className="absolute top-[60%] right-[18%] text-xs font-bold bg-white border border-black px-0.5">RUN</div>
            </div>
            <div
                className={`bg-white border border-gray-300 shadow-sm rounded-md px-8 py-1 font-bold text-lg ${onClick ? 'cursor-pointer hover:text-blue-600 hover:border-blue-500 transition-colors' : ''}`}
                onClick={onClick}
            >
                {name}
            </div>
        </div>
    );
};

const WaterLevelChartModal = ({ isOpen, onClose, locationId }) => {
    const [wl1Data, setWl1Data] = useState([]);
    const [wl2Data, setWl2Data] = useState([]);
    const [wl3Data, setWl3Data] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen && locationId) {
            const fetchChartData = async () => {
                setLoading(true);
                try {
                    const [data1, data2, data3] = await Promise.all([
                        dashboardService.getWaterLevelHistory(`${locationId}_WL1`),
                        dashboardService.getWaterLevelHistory(`${locationId}_WL2`),
                        dashboardService.getWaterLevelHistory(`${locationId}_WL3`)
                    ]);
                    setWl1Data(data1);
                    setWl2Data(data2);
                    setWl3Data(data3);
                } catch (error) {
                    console.error("Failed to fetch water level history:", error);
                } finally {
                    setLoading(false);
                }
            };
            fetchChartData();
        }
    }, [isOpen, locationId]);

    if (!isOpen) return null;

    const Chart = ({ data, title, color }) => (
        <div className="p-4 border rounded-lg shadow-sm bg-gray-50">
            <h4 className="font-semibold text-gray-700 mb-2">{title}</h4>
            <ResponsiveContainer width="100%" height={200}>
                <LineChart data={data} margin={{ top: 5, right: 20, left: 15, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis dataKey="time" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} label={{ value: 'Level (m)', angle: -90, position: 'insideLeft', offset: 10, fontSize: 12 }} />
                    <Tooltip position={{ y: 0 }} animationDuration={0} formatter={(value) => `${value.toFixed(2)} m`} />
                    <Line isAnimationActive={false} type="monotone" dataKey="value" stroke={color} strokeWidth={2} dot={false} activeDot={{ r: 6 }} />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 transition-opacity duration-300"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-lg shadow-xl p-6 w-full max-w-5xl transform transition-all duration-300 scale-95 opacity-0 animate-fade-in-scale"
                onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside modal
            >
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold text-gray-800">Water Level History (Last 6 Hours)</h2>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-200">
                        <X size={24} />
                    </button>
                </div>
                {loading ? (
                    <div className="text-center py-20">Loading charts...</div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Chart data={wl1Data} title="Water Level 1 (SEA)" color="#3b82f6" />
                        <Chart data={wl2Data} title="Water Level 2 (SUMP)" color="#8b5cf6" />
                        <Chart data={wl3Data} title="Water Level 3 (DRAIN)" color="#10b981" />
                    </div>
                )}
            </div>
        </div>
    );
};

const PumpHistoryChartModal = ({ isOpen, onClose, pumpName, locationId }) => {
    const [historyData, setHistoryData] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen && pumpName && locationId) {
            const fetchHistory = async () => {
                setLoading(true);
                try {
                    const hours = 12;
                    const data = pumpName === 'PUMP 1'
                        ? await dashboardService.getPump1History(locationId, hours)
                        : await dashboardService.getPump2History(locationId, hours);

                    // Extract the array (pump1Activity or pump2Activity)
                    const activityKey = Object.keys(data).find(k => k.includes('Activity'));
                    const list = activityKey ? data[activityKey] : [];

                    // Reverse to show oldest to newest left-to-right
                    setHistoryData([...list].reverse());
                } catch (error) {
                    console.error("Failed to fetch pump history:", error);
                } finally {
                    setLoading(false);
                }
            };
            fetchHistory();
        }
    }, [isOpen, pumpName, locationId]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 transition-opacity duration-300" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-4xl transform transition-all duration-300 scale-95 opacity-0 animate-fade-in-scale" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold text-gray-800">{pumpName} History (Last 12 Hours)</h2>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-200"><X size={24} /></button>
                </div>
                {loading ? <div className="text-center py-20">Loading history...</div> : (
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={historyData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="time" />
                            <YAxis domain={[0, 1]} ticks={[0, 1]} tickFormatter={v => v === 1 ? 'ON' : 'OFF'} />
                            <Tooltip formatter={v => v === 1 ? 'ON' : 'OFF'} />
                            <Line type="stepAfter" dataKey="value" stroke="#2563eb" strokeWidth={2} dot={false} />
                        </LineChart>
                    </ResponsiveContainer>
                )}
            </div>
        </div>
    );
};

const WeatherForecastWidget = ({ forecastData = [], radarUrl = null, isLoading = false }) => {
    const [activeTab, setActiveTab] = useState('radar'); // 'forecast' | 'radar'

    const getIconForForecast = (forecast) => {
        const forecastLower = forecast?.toLowerCase() || '';
        if (forecastLower.includes('ribut petir')) return <CloudLightning className="w-10 h-10 text-yellow-400" />;
        if (forecastLower.includes('hujan')) return <CloudRain className="w-10 h-10 text-blue-400" />;
        if (forecastLower.includes('tiada hujan') || forecastLower.includes('cerah')) return <Sun className="w-10 h-10 text-orange-400" />;
        return <Cloud className="w-10 h-10 text-gray-400" />;
    };

    const formatDay = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();
    };

    const today = forecastData?.[0];

    // Show loading only during initial fetch, not when API has failed
    if (isLoading && !today?.location) {
        return (
            <div className="bg-white p-4 rounded-lg shadow-md h-[350px] flex items-center justify-center">
                <div className="text-gray-500">Loading weather data...</div>
            </div>
        );
    }

    // Forecast tab content: show data or unavailable message
    const renderForecastContent = () => {
        if (!today?.location) {
            return (
                <div className="flex-grow flex flex-col items-center justify-center text-gray-400 h-full">
                    <Cloud className="w-12 h-12 mb-2 opacity-50" />
                    <p className="text-sm font-semibold">Weather data unavailable</p>
                    <p className="text-xs mt-1">Will retry automatically</p>
                </div>
            );
        }
        return (
            <div className="flex flex-col h-full justify-between animate-fade-in">
                <div className="flex items-center gap-4 mb-3">
                    <div className="flex-shrink-0">{getIconForForecast(today.summary_forecast)}</div>
                    <div className="flex-grow">
                        <p className="text-2xl font-bold text-gray-800">{today.max_temp}°C</p>
                        <p className="text-sm text-gray-500 capitalize">{today.summary_forecast}</p>
                    </div>
                    <div className="text-right text-sm text-gray-600">
                        <p className="font-bold text-gray-800">H: {today.max_temp}°</p>
                        <p>L: {today.min_temp}°</p>
                    </div>
                </div>
                <div className="flex space-x-4 overflow-x-auto pb-2 scrollbar-hide" style={{ height: '140px' }}>
                    {forecastData.map((day, index) => (
                        <div key={index} className="flex flex-col items-center justify-between flex-shrink-0 w-20 h-full p-2 rounded-lg bg-gray-50 border">
                            <p className="text-sm font-bold text-gray-600">{formatDay(day.date)}</p>
                            <div className="my-1 scale-90">{getIconForForecast(day.summary_forecast)}</div>
                            <p className="text-xs text-gray-500">{day.max_temp}°/{day.min_temp}°</p>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="bg-white p-4 rounded-lg shadow-md flex flex-col min-h-[350px]">
            {/* Header with Tabs */}
            <div className="flex justify-between items-center mb-4 border-b pb-2">
                <h4 className="font-bold text-blue-600 truncate max-w-[50%]">
                    {activeTab === 'forecast' ? 'MELAKA' : 'MET Radar'}
                </h4>
                <div className="flex bg-gray-100 rounded-lg p-1">
                    <button
                        onClick={() => setActiveTab('forecast')}
                        className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${activeTab === 'forecast' ? 'bg-white text-blue-600 shadow-sm' : 'text-white hover:text-gray-700'
                            }`}
                    >
                        Forecast
                    </button>
                    <button
                        onClick={() => setActiveTab('radar')}
                        className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${activeTab === 'radar' ? 'bg-white text-blue-600 shadow-sm' : 'text-white hover:text-gray-700'
                            }`}
                    >
                        Radar
                    </button>
                </div>
            </div>

            {/* Content */}
            {activeTab === 'forecast' ? renderForecastContent() : (
                <div className="flex-grow flex items-center justify-center bg-gray-100 rounded-lg overflow-hidden relative h-full">
                    {radarUrl ? (
                        <img
                            src={radarUrl}
                            alt="Radar"
                            className="w-full h-full object-cover"
                        />
                    ) : (
                        <div className="flex flex-col items-center text-gray-400">
                            <Cloud className="w-8 h-8 mb-1 opacity-50" />
                            <div className="text-xs">{isLoading ? 'Loading Radar...' : 'Radar unavailable'}</div>
                        </div>
                    )}
                    {radarUrl && (
                        <div className="absolute top-2 right-2 bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 rounded shadow animate-pulse">
                            LIVE
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// const CustomizedDot = (props) => {
//     const { cx, cy, payload } = props;

//     // Only render custom labels for specific points (high/low tides)
//     if (payload.isPeak) {
//       const isHighTide = payload.level > 1.5;
//       const yOffset = isHighTide ? -45 : 15;
//       const arrow = isHighTide ? '↑' : '↓';

//       return (
//         <>
//           <circle cx={cx} cy={cy} r={4} stroke="#3b82f6" strokeWidth={2} fill="#fff" />
//           <foreignObject x={cx - 50} y={cy + yOffset} width={100} height={40}>
//             <div className="text-center bg-white/80 backdrop-blur-sm border border-gray-300 rounded-md p-1 shadow-lg">
//               <div className="text-xs font-bold text-gray-800">{payload.time}</div>
//               <div className={`text-xs font-semibold ${isHighTide ? 'text-blue-600' : 'text-green-600'}`}>
//                 {arrow} {payload.level.toFixed(1)}m
//               </div>
//             </div>
//           </foreignObject>
//         </>
//       );
//     }

//     return null; // Render nothing for normal points
// };


const Dashboardv3Page = () => {
    const [time, setTime] = useState(new Date());
    const [dashboardData, setDashboardData] = useState(null);
    const [selectedLocation, setSelectedLocation] = useState(LOCATIONS[0]);
    const [mapLocations, setMapLocations] = useState([]);
    const [weatherForecast, setWeatherForecast] = useState([]);
    const [isChartModalOpen, setIsChartModalOpen] = useState(false);
    const [isPumpHistoryOpen, setIsPumpHistoryOpen] = useState(false);
    const [selectedPump, setSelectedPump] = useState(null);
    const [radarGifUrl, setRadarGifUrl] = useState(null);
    const { logout } = useAuth();

    useEffect(() => {
        const timer = setInterval(() => setTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    // Update map locations active status based on selection
    useEffect(() => {
        setMapLocations(prev => {
            if (!prev || prev.length === 0) return prev;
            return prev.map(loc => ({
                ...loc,
                active: loc.id === selectedLocation.id
            }));
        });
    }, [selectedLocation]);

    const handlePumpClick = (pumpName) => {
        setSelectedPump(pumpName);
        setIsPumpHistoryOpen(true);
    };

    // This useEffect hook fetches all necessary data from the backend services.
    useEffect(() => {
        let activeRadarUrl = null;

        const processForecastData = (apiResponse) => {
            if (!apiResponse || !apiResponse.results || apiResponse.results.length === 0) {
                return [];
            }

            const results = apiResponse.results;

            const groupedByDate = results.reduce((acc, curr) => {
                const date = curr.date.split('T')[0];
                if (!acc[date]) {
                    acc[date] = {
                        date: curr.date,
                        location: { location_name: curr.locationname },
                        max_temp: null,
                        min_temp: null,
                        summary_forecast: 'N/A',
                    };
                }

                if (curr.datatype === 'FMAXT') acc[date].max_temp = curr.value;
                if (curr.datatype === 'FMINT') acc[date].min_temp = curr.value;
                if (curr.datatype === 'FSIGW') acc[date].summary_forecast = curr.value;
                if (curr.datatype === 'FGM' && (!acc[date].summary_forecast || acc[date].summary_forecast === 'N/A')) acc[date].summary_forecast = curr.value;

                return acc;
            }, {});

            return Object.values(groupedByDate);
        }

        const fetchData = async () => {
            try {
                // Fetch all data in parallel using allSettled so individual failures
                // don't block the entire dashboard from loading
                const [statusResult, locationsResult, forecastResult, radarResult] = await Promise.allSettled([
                    dashboardService.getPumpHouseV2Status(selectedLocation.id),
                    dashboardService.getMapLocations(),
                    dashboardService.getWeatherForecast(),
                    dashboardService.getMetRadarGif(),
                ]);

                // Critical data: pump house status
                if (statusResult.status === 'fulfilled') {
                    setDashboardData(statusResult.value);
                } else {
                    console.error('Failed to fetch pump house status:', statusResult.reason);
                    // Still set dashboardData so loading overlay clears
                    setDashboardData(prev => prev || {});
                }

                // Map locations
                if (locationsResult.status === 'fulfilled') {
                    setMapLocations(locationsResult.value);
                } else {
                    console.error('Failed to fetch map locations:', locationsResult.reason);
                }

                // Weather forecast (non-critical — dashboard still works without it)
                if (forecastResult.status === 'fulfilled') {
                    setWeatherForecast(processForecastData(forecastResult.value));
                } else {
                    console.error('Failed to fetch weather forecast:', forecastResult.reason);
                }

                // Radar GIF (non-critical — dashboard still works without it)
                if (radarResult.status === 'fulfilled') {
                    const radarBlob = radarResult.value;
                    if (radarBlob instanceof Blob) {
                        if (activeRadarUrl) {
                            URL.revokeObjectURL(activeRadarUrl);
                        }
                        const newUrl = URL.createObjectURL(radarBlob);
                        activeRadarUrl = newUrl;
                        setRadarGifUrl(newUrl);
                    }
                } else {
                    console.error('Failed to fetch radar GIF:', radarResult.reason);
                }
            } catch (err) {
                console.error('Unexpected error during data fetch:', err);
                // Still clear loading state even on unexpected errors
                setDashboardData(prev => prev || {});
            }
        };

        fetchData();
        const interval = setInterval(fetchData, 120000); // Refresh every 2 minute
        return () => {
            clearInterval(interval);
            if (activeRadarUrl) {
                URL.revokeObjectURL(activeRadarUrl);
            }
        };
    }, [selectedLocation.id]);

    // Keep a ref to the latest selectedLocation so the WebSocket
    // message handler always sees the current value without needing
    // to reconnect every time the user switches location.
    const selectedLocationRef = useRef(selectedLocation);
    useEffect(() => {
        selectedLocationRef.current = selectedLocation;
    }, [selectedLocation]);

    // WebSocket connection for live updates — connects once and stays open.
    // Messages are filtered by the current selectedLocation via the ref.
    useEffect(() => {
        let socket;
        let reconnectTimer;

        const connect = () => {
            const wsUrl = process.env.REACT_APP_WS_URL || 'wss://nodered.netricsolutions.com/status';
            socket = new WebSocket(wsUrl);

            socket.onopen = () => {
                console.log('WebSocket Connected');
            };

            socket.onmessage = (event) => {
                try {
                    const update = JSON.parse(event.data);
                    // Only apply updates that match the currently selected location
                    if (update.locationId && update.locationId !== selectedLocationRef.current.id) {
                        return;
                    }
                    setDashboardData(prev => {
                        if (!prev) return update;
                        return { ...prev, ...update };
                    });
                } catch (err) {
                    console.error('WebSocket message parse error:', err);
                }
            };

            socket.onclose = () => {
                // Attempt to reconnect after 5 seconds
                reconnectTimer = setTimeout(connect, 5000);
            };

            socket.onerror = (err) => {
                console.error('WebSocket Error:', err);
                socket.close();
            };
        };

        connect();

        return () => {
            if (socket) socket.close();
            if (reconnectTimer) clearTimeout(reconnectTimer);
        };
    }, []); // No dependency — single persistent connection

    const fallbackData = {
        pump1: { value: 0, status: 'OFF' },
        pump2: { value: 0, status: 'OFF' },
        waterLevels: { sea: 0, sump: 0, drain: 0 }
    };
    const activeData = dashboardData || fallbackData;

    return (
        <div className="dashboard-container relative min-h-screen">
            {/* Main Content (Blurred during loading) */}
            <div className={`transition-all duration-500 ${!dashboardData ? 'filter blur-[5px] pointer-events-none select-none' : ''}`}>
                <header className="header">
                    <div className="flex items-center space-x-3">
                        <img src={logo} alt="Logo" className="h-10 md:h-16" />
                        <div className="flex flex-col items-start">
                            <h2 className="text-sm md:text-xl text-gray-800 font-bold leading-tight">MAJLIS BANDARAYA MELAKA BERSEJARAH</h2>
                            <h1 className="text-xs md:text-lg text-gray-600 leading-tight">SPOP 2.0</h1>
                        </div>
                    </div>

                    {/* Location Selector Dropdown — full width row on mobile */}
                    <div className="flex items-center bg-gray-50 px-4 py-2 rounded-full border border-gray-200 w-full md:w-auto order-last md:order-none">
                        <span className="text-gray-500 text-sm font-semibold mr-2 whitespace-nowrap">RUMAH PAM:</span>
                        <select
                            value={selectedLocation.id}
                            onChange={(e) => {
                                const newLoc = LOCATIONS.find(loc => loc.id === e.target.value);
                                setSelectedLocation(newLoc);
                                setDashboardData(null); // Clear telemetry so loading triggers
                            }}
                            className="bg-transparent text-gray-800 text-sm font-bold outline-none cursor-pointer appearance-none pr-6 flex-1"
                            style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23374151' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right center' }}
                        >
                            {LOCATIONS.map(loc => (
                                <option key={loc.id} value={loc.id}>{loc.id} - {loc.name}</option>
                            ))}
                        </select>
                    </div>

                    {/* Desktop buttons — hidden on mobile */}
                    <div className="hidden md:flex items-center space-x-3">
                        <button onClick={() => setIsChartModalOpen(true)} className="text-white bg-blue-600 hover:bg-blue-700 p-2.5 rounded-full transition-colors shadow-sm">
                            <ChartNoAxesCombined size={20} />
                        </button>
                        <button className="text-white bg-blue-600 hover:bg-blue-700 p-2.5 rounded-full transition-colors shadow-sm">
                            <Settings size={20} />
                        </button>
                        <button onClick={logout} className="text-white bg-blue-600 hover:bg-blue-700 p-2.5 rounded-full transition-colors shadow-sm">
                            <LogOut size={20} />
                        </button>
                    </div>
                </header>

                {/* Mobile Bottom Navigation Bar */}
                <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-gray-50 border-t border-gray-200">
                    <div className="flex justify-around items-center h-16 px-2">
                        <button onClick={() => setIsChartModalOpen(true)} className="flex flex-col items-center justify-center gap-1 min-w-[64px]">
                            <ChartNoAxesCombined size={22} className="text-gray-600" strokeWidth={1.5} />
                            <span className="text-[11px] text-gray-600">Charts</span>
                        </button>
                        <button className="flex flex-col items-center justify-center gap-1 min-w-[64px]">
                            <Settings size={22} className="text-gray-600" strokeWidth={1.5} />
                            <span className="text-[11px] text-gray-600">Settings</span>
                        </button>
                        <button onClick={logout} className="flex flex-col items-center justify-center gap-1 min-w-[64px]">
                            <LogOut size={22} className="text-gray-600" strokeWidth={1.5} />
                            <span className="text-[11px] text-gray-600">Logout</span>
                        </button>
                    </div>
                </nav>

                <WaterLevelChartModal isOpen={isChartModalOpen} onClose={() => setIsChartModalOpen(false)} locationId={selectedLocation.id} />
                <PumpHistoryChartModal isOpen={isPumpHistoryOpen} onClose={() => setIsPumpHistoryOpen(false)} pumpName={selectedPump} locationId={selectedLocation.id} />

                <main className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {/* Left Column */}
                    <div className="lg:col-span-1 md:col-span-1 col-span-1 space-y-4">
                        <div className="bg-blue-900 text-white p-4 rounded-lg shadow-md text-center">
                            <h3 className="text-5xl font-bold">{time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</h3>
                            <p className="text-lg">{time.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })}</p>
                        </div>
                        <WeatherForecastWidget
                            forecastData={weatherForecast}
                            radarUrl={radarGifUrl}
                            isLoading={!dashboardData}
                        />
                        <div className="bg-[#e4e8edff] p-4 rounded-lg shadow-md min-h-[310px]">
                            <TideChart lat={selectedLocation.lat} lon={selectedLocation.lon} />
                        </div>
                    </div>

                    {/* Middle Column */}
                    <div className="lg:col-span-2 md:col-span-2 col-span-1 space-y-4">
                        <div className="bg-white p-4 rounded-lg shadow-md">
                            <h4 className="font-bold mb-2 text-black">MONITORING MAP LOCATION</h4>
                            <div style={{ height: '450px', width: '100%' }}><LeafletMap markers={mapLocations} /></div>
                        </div>
                        <div className="bg-white px-4 py-12 rounded-lg shadow-md">
                            <h3 className="font-bold text-lg text-black mb-2 text-center">RUMAH PAM {selectedLocation.name}</h3>
                            <div className="grid grid-cols-1 text-black md:grid-cols-2 gap-4">
                                <PumpGauge
                                    name="PUMP 1"
                                    value={activeData.pump1?.value || 0}
                                    status={activeData.pump1?.status || 'UNKNOWN'}
                                    onClick={() => handlePumpClick('PUMP 1')}
                                />
                                <PumpGauge
                                    name="PUMP 2"
                                    value={activeData.pump2?.value || 0}
                                    status={activeData.pump2?.status || 'UNKNOWN'}
                                    onClick={() => handlePumpClick('PUMP 2')}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Right Column */}
                    <div className="lg:col-span-1 md:col-span-3 col-span-1 space-y-4">
                        <div className="bg-white p-4 rounded-lg shadow-md">
                            <h4 className="font-bold mb-2 text-black">CAM 1</h4>
                            <div className="bg-black h-80">
                                <img
                                    src={selectedLocation.id === 'PH3' ? '/cam3img.jpeg' : selectedLocation.id === 'PH2' ? '/cam2img.jpeg' : '/cam1img.jpeg'}
                                    alt="Camera 1 Feed"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        </div>
                        <div className="relative p-4 rounded-lg shadow-md flex items-end justify-between pb-4" style={{
                            backgroundImage: "url('/house2.png')",
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat',
                            minHeight: '420px'
                        }}>
                            <div className="relative w-1/2 h-[220px] -ml-2">
                                <WaterLevelGraphic levels={activeData.waterLevels} locationId={selectedLocation.id} />
                            </div>
                            <div className="w-1/2 flex justify-end pr-2">
                                <ThresholdLegend />
                            </div>
                        </div>
                    </div>
                </main>
            </div>

            {/* Premium Loading Glassmorphic Overlay (Fades out when data arrives) */}
            {!dashboardData && (
                <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-slate-900/10 backdrop-blur-[4px] transition-all duration-500 animate-fade-in">
                    <div className="flex flex-col items-center p-8 bg-white/85 backdrop-blur-md rounded-2xl border border-slate-200/50 shadow-[0_20px_50px_rgba(0,0,0,0.15)]">
                        {/* Modern Spinner */}
                        <div className="w-12 h-12 border-4 border-blue-500/20 border-t-blue-600 rounded-full animate-spin"></div>
                        <span className="mt-4 text-sm font-extrabold tracking-wider text-slate-700 uppercase animate-pulse">
                            Fetching Live Telemetry...
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Dashboardv3Page;